<template>
  <div class="real-time-table-wrapper" :class="{ 'real-time-table-wrapper--mobile': isMobileViewport }">
  <section v-if="!isMobileViewport" class="real-time-table">
    <div
      :class="['table-head', 'table-row', 'sticky-table-head', { task: isTask }]"
      :style="{ gridTemplateColumns }"
    >
      <div
        :class="[
          'table-cell',
          `table-cell--${title}`,
          title === 'visits_since_cleaned' ? 'table-cell--visits' : '',
          index === headerTitles.length - 1 ? 'last' : '',
          index === sortedTitleIdx ? 'sorted' : '',
        ]"
        v-for="(title, index) in headerTitles"
        :style="{ 'grid-column-start': index + 1 }"
        :key="title"
        @click="handleSortClick(title, index)"
      >
        <span class="table-head-label">
          <template v-if="title === 'cleandex'">
            {{ $t(title) }}
            <Tooltip
              :text="$t('cleandex_column_tooltip')"
              placement="bottom"
              class="cleandex-header-info"
              @click.native.stop
            >
              <span class="cleandex-info-icon" @click.stop aria-hidden="true">ⓘ</span>
            </Tooltip>
          </template>
          <template v-else>
            {{ getColumnHeaderLabel(title) }}
          </template>
        </span>
      </div>
    </div>
    <template v-for="(room, index) in sortedRealTimeData">
      <div
        :key="room.id != null ? room.id : index"
        :class="['table-row', { 'open-comlaint-row': room.isOpen && isManualCloseSla, task: isTask, 'table-row--expanded': showHistory && selectedRoom && selectedRoom.id === room.id }]"
        :style="{ ...getSlaRowBgColor(room), gridTemplateColumns }"
        @click="showHistory ? onRowClick(room) : null"
      >
        <RealTimeTableCell
          v-for="(header, headerIndex) in headerTitles"
          :class="['table-cell', `table-cell--${header}`, header === 'visits_since_cleaned' ? 'table-cell--visits' : '']"
          :maxSla="room.sla || maxSla"
          :key="header"
          :style="{ 'grid-column-start': headerIndex + 1 }"
          :columnName="header"
          :slaData="room"
          :isMaintenance="isMaintenance"
          :companyGmt="companyGmt"
        />
        <div class="buttons-container" v-if="room.isOpen && isManualCloseSla" @click.stop>
          <button v-if="isTask" class="start-button" @click="startTask(room)">
            <img src="@/assets/start-icon.svg" alt="start" />
          </button>
          <button class="close-button" @click="isTask ? completeTask(room) : emitCloseRoom(room)">
            <img src="@/assets/x-icon.svg" alt="X" />
          </button>
          <button v-if="isTask" class="delete-button" @click="deleteOpenTask(room)" :title="$t('delete')">
            <img src="@/assets/delete-icon.svg" alt="delete" />
          </button>
        </div>
      </div>
      <!-- Inline history row: only in clean tab -->
      <div
        v-if="showHistory && selectedRoom && selectedRoom.id === room.id"
        :key="'detail-' + (room.id != null ? room.id : index)"
        class="room-detail-inline"
      >
        <div class="room-detail-inline-inner" :dir="isRTL ? 'rtl' : 'ltr'">
          <div class="room-detail-inline-header">
            <span class="room-detail-inline-title">{{ room.name }} — {{ $t('callsHistory') }}</span>
            <button type="button" class="room-detail-inline-close" aria-label="Close" @click.stop="closeRoomDetail">×</button>
          </div>
          <p v-if="getRoomDetailsText(room)" class="room-detail-inline-meta">{{ getRoomDetailsText(room) }}</p>
          <div class="room-detail-inline-timeline">
            <span class="room-detail-inline-label">{{ $t('callsHistory') }}</span>
            <div v-if="loadingClosed" class="room-detail-inline-loading">{{ $t('loading') }}...</div>
            <div v-else class="room-detail-inline-row">
              <template v-for="(event, i) in roomHistoryTimeline">
                <span v-if="i > 0" class="room-detail-inline-arrow" aria-hidden="true">{{ isRTL ? '←' : '→' }}</span>
                <span :key="event.key" class="room-detail-inline-chip" :class="{ 'room-detail-inline-chip--open': event.isOpen }" :style="getComplaintChipStyle(event)">
                  <Tooltip v-if="getComplaintIconTitle(event)" :text="getComplaintIconTitle(event)" class="room-detail-inline-chip-tooltip">
                    <span class="room-detail-inline-chip-content">
                      <img v-if="getComplaintIcon(event)" :src="getComplaintIcon(event)" alt="" class="room-detail-inline-chip-icon" />
                      {{ formatDialogTime(event.time) }}<span v-if="event.count > 1">×{{ event.count }}</span>
                      <span v-if="event.isOpen" class="room-detail-inline-chip-open-badge" :title="$t('openCalls')">●</span>
                    </span>
                  </Tooltip>
                  <span v-else class="room-detail-inline-chip-content">
                    <img v-if="getComplaintIcon(event)" :src="getComplaintIcon(event)" alt="" class="room-detail-inline-chip-icon" />
                    {{ formatDialogTime(event.time) }}<span v-if="event.count > 1">×{{ event.count }}</span>
                    <span v-if="event.isOpen" class="room-detail-inline-chip-open-badge" :title="$t('openCalls')">●</span>
                  </span>
                </span>
              </template>
              <span v-if="!loadingClosed && roomHistoryTimeline.length === 0" class="room-detail-inline-empty">{{ $t('no_data_available') }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>

  <section v-else class="real-time-mobile-list">
    <template v-for="(room, index) in sortedRealTimeData">
      <RealTimeMobileCard
        :key="room.id != null ? room.id : index"
        :room="room"
        :fields="headerTitles"
        :maxSla="room.sla || maxSla"
        :isMaintenance="isMaintenance"
        :isTask="isTask"
        :companyGmt="companyGmt"
        :isManualCloseSla="isManualCloseSla"
        :is-expanded="showHistory && selectedRoom && selectedRoom.id === room.id"
        @card-click="onRowClick(room)"
        @close-click="onMobileActionClose(room)"
        @start-task="startTask"
        @delete-open-task="deleteOpenTask"
      />
      <div
        v-if="showHistory && selectedRoom && selectedRoom.id === room.id"
        :key="'mobile-detail-' + (room.id != null ? room.id : index)"
        class="room-detail-inline room-detail-inline--mobile"
      >
        <div class="room-detail-inline-inner" :dir="isRTL ? 'rtl' : 'ltr'">
          <div class="room-detail-inline-header">
            <span class="room-detail-inline-title">{{ room.name }} — {{ $t('callsHistory') }}</span>
            <button type="button" class="room-detail-inline-close" aria-label="Close" @click.stop="closeRoomDetail">×</button>
          </div>
          <p v-if="getRoomDetailsText(room)" class="room-detail-inline-meta">{{ getRoomDetailsText(room) }}</p>
          <div class="room-detail-inline-timeline">
            <span class="room-detail-inline-label">{{ $t('callsHistory') }}</span>
            <div v-if="loadingClosed" class="room-detail-inline-loading">{{ $t('loading') }}...</div>
            <div v-else class="room-detail-inline-row">
              <template v-for="(event, i) in roomHistoryTimeline">
                <span v-if="i > 0" class="room-detail-inline-arrow" aria-hidden="true">{{ isRTL ? '←' : '→' }}</span>
                <span :key="event.key" class="room-detail-inline-chip" :class="{ 'room-detail-inline-chip--open': event.isOpen }" :style="getComplaintChipStyle(event)">
                  <Tooltip v-if="getComplaintIconTitle(event)" :text="getComplaintIconTitle(event)" class="room-detail-inline-chip-tooltip">
                    <span class="room-detail-inline-chip-content">
                      <img v-if="getComplaintIcon(event)" :src="getComplaintIcon(event)" alt="" class="room-detail-inline-chip-icon" />
                      {{ formatDialogTime(event.time) }}<span v-if="event.count > 1">×{{ event.count }}</span>
                      <span v-if="event.isOpen" class="room-detail-inline-chip-open-badge" :title="$t('openCalls')">●</span>
                    </span>
                  </Tooltip>
                  <span v-else class="room-detail-inline-chip-content">
                    <img v-if="getComplaintIcon(event)" :src="getComplaintIcon(event)" alt="" class="room-detail-inline-chip-icon" />
                    {{ formatDialogTime(event.time) }}<span v-if="event.count > 1">×{{ event.count }}</span>
                    <span v-if="event.isOpen" class="room-detail-inline-chip-open-badge" :title="$t('openCalls')">●</span>
                  </span>
                </span>
              </template>
              <span v-if="!loadingClosed && roomHistoryTimeline.length === 0" class="room-detail-inline-empty">{{ $t('no_data_available') }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>
  </div>
</template>

<script>
import RealTimeTableCell from '../components/RealTimeTableCell'
import RealTimeMobileCard from '../components/RealTimeMobileCard'
import Tooltip from '@/modules/common/components/Tooltip'
import realTimeService from '../services/realTimeService'
import utilService from '../../common/services/utilService'
import { isOfflineTabletDisplay } from '../utils/deviceConnectivityUtils'
import { servikMap } from '@/modules/surveys/consts/consts'
import { mapGetters } from 'vuex'

/** Per-column grid tracks: narrow for metrics/icons, wider for name and calls */
const COLUMN_GRID_WIDTHS = {
  name: { default: 'minmax(5.5rem, 1.5fr)', tablet: 'minmax(4.5rem, 1.2fr)' },
  temperature: { default: 'minmax(3.25rem, 0.45fr)', tablet: 'minmax(3rem, 0.4fr)' },
  humidity: { default: 'minmax(3.25rem, 0.45fr)', tablet: 'minmax(3rem, 0.4fr)' },
  floor: { default: 'minmax(2rem, 0.38fr)', tablet: 'minmax(2.25rem, 0.35fr)' },
  gender: { default: 'minmax(2.25rem, 0.32fr)', tablet: 'minmax(2.5rem, 0.3fr)' },
  arrival: { default: 'minmax(3rem, 0.5fr)', tablet: 'minmax(2.75rem, 0.45fr)' },
  arrival_badges: { default: 'minmax(2.25rem, 0.32fr)', tablet: 'minmax(2.25rem, 0.3fr)' },
  visits_since_cleaned: { default: 'minmax(2.5rem, 0.38fr)', tablet: 'minmax(2.5rem, 0.35fr)' },
  sla_status: { default: 'minmax(2.25rem, 0.34fr)', tablet: 'minmax(2.5rem, 0.32fr)' },
  duration: { default: 'minmax(3rem, 0.52fr)', tablet: 'minmax(2.75rem, 0.48fr)' },
  call: { default: 'minmax(4.5rem, 2.8fr)', tablet: 'minmax(4rem, 2.2fr)' },
  cleandex: { default: 'minmax(2.5rem, 0.38fr)', tablet: 'minmax(2.5rem, 0.35fr)' },
  time_start: { default: 'minmax(4rem, 0.72fr)', tablet: 'minmax(3.5rem, 0.65fr)' },
  date: { default: 'minmax(4rem, 0.72fr)', tablet: 'minmax(3.5rem, 0.65fr)' },
  assignee: { default: 'minmax(4rem, 0.85fr)', tablet: 'minmax(3.5rem, 0.75fr)' },
  title: { default: 'minmax(4.5rem, 1.25fr)', tablet: 'minmax(4rem, 1.1fr)' },
  building: { default: 'minmax(2rem, 0.38fr)', tablet: 'minmax(2.25rem, 0.35fr)' },
}

const ACTIONS_COLUMN = { default: 'minmax(1.75rem, 0.22fr)', tablet: 'minmax(2rem, 0.2fr)' }
const MOBILE_BREAKPOINT = '(max-width: 768px)'

export default {
  components: { RealTimeTableCell, RealTimeMobileCard, Tooltip },
  props: {
    realTimeData: {
      /**
       * @type {new() {
       * name: String;
       * building: String;
       * zone: String;
       * floor: String;
       * gender: String;
       * isOpen: Boolean;
       * arrival: {
       *    count:number;
       *    time: string;
       * }
       * openTime: String | Number;
       * complaints: {
       * name: String;
       * time: String | Number;
       * count: Number
       * }[]
       * }[]}
       */
      type: Array,
      required: true,
    },
    //number of minutes
    maxSla: { type: Number, required: false },
    isMaintenance: { type: Boolean, default: false, required: false },
    isTask: { type: Boolean, default: false, required: false },
    sortBy: { type: String, default: 'duration', required: false },
    /** Company GMT offset for displaying times in company timezone (e.g. 8 for GMT+8). */
    companyGmt: { type: [Number, String], default: null, required: false },
    siteId: { type: [Number, String], default: null, required: false },
    roomSearchText: { type: String, default: '' },
  },
  data() {
    return {
      currentSort: null,
      currentSortDir: 'original',
      sortedTitleIdx: null,
      selectedRoom: null,
      closedComplaints: [],
      loadingClosed: false,
      isMobileViewport: false,
      _mobileMediaQuery: null,
    }
  },
  created() {
    this.currentSort = this.sortBy
  },
  mounted() {
    if (typeof window === 'undefined' || !window.matchMedia) return
    this._mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT)
    this.isMobileViewport = this._mobileMediaQuery.matches
    this._onMobileMediaQueryChange = (event) => {
      this.isMobileViewport = event.matches
    }
    if (this._mobileMediaQuery.addEventListener) {
      this._mobileMediaQuery.addEventListener('change', this._onMobileMediaQueryChange)
    } else {
      this._mobileMediaQuery.addListener(this._onMobileMediaQueryChange)
    }
  },
  beforeDestroy() {
    if (!this._mobileMediaQuery || !this._onMobileMediaQueryChange) return
    if (this._mobileMediaQuery.removeEventListener) {
      this._mobileMediaQuery.removeEventListener('change', this._onMobileMediaQueryChange)
    } else {
      this._mobileMediaQuery.removeListener(this._onMobileMediaQueryChange)
    }
  },
  methods: {
    emitCloseRoom(room) {
      this.$emit('closeClick', room.id)
    },
    startTask({ slaId }) {
      this.$emit('startTask', slaId)
    },
    completeTask({ slaId }) {
      this.$emit('completeTask', slaId)
    },
    deleteOpenTask(room) {
      this.$emit('deleteOpenTask', room.slaId)
    },
    onMobileActionClose(room) {
      if (this.isTask) this.completeTask(room)
      else this.emitCloseRoom(room)
    },
    handleSortClick(value, valueIdx) {
      this.sortedTitleIdx = this.sortedTitleIdx === valueIdx ? '' : valueIdx
      if (value === this.currentSort) this.currentSortDir = this.currentSortDir === 'asc' ? 'original' : 'asc'
      if (value != this.currentSort) {
        this.currentSortDir = 'asc'
        this.currentSort = value
      }
    },
    getSlaRowBgColor(room) {
      let bgColor = ''
      const { sensorState, sensorType } = room
      if (sensorType === 'pad9' || sensorType === 'pad9+') {
        if (!sensorState || sensorState === 'offline') bgColor = '#F5F4F4'
      } else if (isOfflineTabletDisplay(room)) {
        bgColor = '#F5F4F4'
      }
      return { backgroundColor: bgColor }
    },
    onRowClick(room) {
      if (!this.showHistory) return
      if (this.selectedRoom && this.selectedRoom.id === room.id) {
        this.closeRoomDetail()
        return
      }
      this.showRoomDialog(room)
    },
    closeRoomDetail() {
      this.selectedRoom = null
      this.closedComplaints = []
    },
    async showRoomDialog(room) {
      this.selectedRoom = room
      this.closedComplaints = []
      this.loadingClosed = true
      try {
        if (room.id && (this.siteId != null && this.siteId !== '')) {
          const list = await realTimeService.getClosedRoomComplaints(room.id, this.siteId)
          this.closedComplaints = Array.isArray(list) ? list : []
        }
      } catch (e) {
        console.error('Failed to load closed room complaints', e)
      } finally {
        this.loadingClosed = false
      }
    },
    formatDialogTime(isoTime) {
      const gmt = this.companyGmt != null && this.companyGmt !== '' ? this.companyGmt : null
      return utilService.formatHMInCompanyTzOrBrowserLocal(isoTime, gmt)
    },
    getRoomDetailsText(room) {
      const parts = []
      if (room.building) parts.push(room.building)
      if (room.floor) parts.push(room.floor)
      if (room.zone) parts.push(room.zone)
      if (room.gender) parts.push(this.$roomTypeLabel(room.gender))
      return parts.length ? parts.join(' / ') : ''
    },
    getComplaintIcon(complaint) {
      if (complaint.iconUrl) return complaint.iconUrl
      if (complaint.url) return complaint.url
      try {
        const category = complaint.category || 'cleaning'
        const id = complaint.servikId ?? complaint.id ?? 0
        const name = (complaint.name || 'default').toLowerCase().replace(/\s+/g, '_')
        return require(`@/assets/survey/servik_icons_white/${category}/${id}.${name}.svg`)
      } catch {
        return ''
      }
    },
    /** Tooltip for icon hover: translation key for servik name (e.g. "clean_the_floor") so $t() shows current language */
    getComplaintIconTitle(event) {
      if (event.name) return event.name
      const sid = Number(event.servikId ?? event.id)
      if (!Number.isFinite(sid)) return ''
      const servik = Object.values(servikMap || {}).flat().find((s) => Number(s.id) === sid)
      if (!servik || !servik.name) return ''
      return servik.name
    },
    getColumnHeaderLabel(title) {
      if (title === 'sla_status') return this.$t('device_status')
      if (title === 'arrival') return this.$t('last_arrival')
      if (title === 'arrival_badges') return this.$t('arrival_badges')
      if (title === 'visits_since_cleaned') return this.$t('visits_since_cleaned')
      if (title === 'temperature') {
        const abbr = this.$t('temperature_abbr')
        return abbr !== 'temperature_abbr' ? abbr : this.$t('temperature')
      }
      if (title === 'humidity') {
        const abbr = this.$t('humidity_abbr')
        return abbr !== 'humidity_abbr' ? abbr : this.$t('humidity')
      }
      return this.$t(title)
    },
    getComplaintChipStyle(complaint) {
      const servikId = complaint.servikId ?? complaint.id
      const { isSuggestion, isOffline, backgroundColor } = complaint
      let bg = ''
      if (servikId === 216 || servikId === 1008) bg = '#AE7EA3'
      else if (isSuggestion) bg = '#DD7ADD'
      else if (isOffline) bg = '#808080'
      else if (backgroundColor) bg = backgroundColor
      else bg = this.isMaintenance ? '#389CEB' : '#fe5060'
      return {
        backgroundColor: bg,
        borderColor: bg,
        color: '#fff',
      }
    },
  },
  computed: {
    ...mapGetters(['loggedUser', 'isRTL']),
    /** History (expandable row) only in clean tab, not maintenance/task/cleandex */
    showHistory() {
      return !this.isMaintenance && !this.isTask
    },
    gridTemplateColumns() {
      const mode = this.isTablet ? 'tablet' : 'default'
      const tracks = this.headerTitles.map((col) => {
        const profile = COLUMN_GRID_WIDTHS[col]
        return profile ? profile[mode] : 'minmax(2rem, 0.4fr)'
      })
      if (this.isManualCloseSla) tracks.push(ACTIONS_COLUMN[mode])
      return tracks.join(' ')
    },
    headerTitles() {
      if (this.isMaintenance)
        return ['name', 'temperature', 'humidity', 'floor', 'gender', 'time_start', 'sla_status', 'duration', 'call']
      if (this.isTask)
        return ['name', 'temperature', 'humidity', 'floor', 'gender', 'assignee', 'date', 'time_start', 'sla_status', 'duration', 'title']
      if (this.isTablet) {
        return [
          'name',
          'temperature',
          'humidity',
          'gender',
          'arrival',
          'arrival_badges',
          'visits_since_cleaned',
          'sla_status',
          'duration',
          'call',
          'cleandex',
        ]
      } else {
        return [
          'name',
          'temperature',
          'humidity',
          'floor',
          'gender',
          'arrival',
          'arrival_badges',
          'visits_since_cleaned',
          'sla_status',
          'duration',
          'call',
          'cleandex',
        ]
      }
    },
    isBuzzAdmin() {
      return this.loggedUser.level === 'buzzz' && this.loggedUser.type === 'admin'
    },
    isFranchiseAdmin() {
      return this.loggedUser.level === 'franchise' && this.loggedUser.type === 'admin'
    },
    isManualCloseSla() {
      if (this.isTask) return true
      const isManager = this.loggedUser.level === 'manager' && this.loggedUser.type === 'viewer'
      return this.isBuzzAdmin || this.isFranchiseAdmin || isManager
    },
    filteredRealTimeData() {
      const q = (this.roomSearchText || '').trim().toLowerCase()
      if (!q) return this.realTimeData
      return this.realTimeData.filter((row) => row.name && row.name.toLowerCase().includes(q))
    },
    sortedRealTimeData() {
      const data = this.filteredRealTimeData
      if (this.currentSortDir === 'original') {
        const hasOpenComplaints = data.some((room) => room.isOpen)
        if (hasOpenComplaints) return data
        return [...data].sort((a, b) => (b.pastVisits ?? 0) - (a.pastVisits ?? 0))
      }
      const value = this.currentSort
      return [...data].sort((a, b) => {
        if (value === 'floor') {
          const aVal = a[value] != null && a[value] !== '' ? +a[value] : Infinity
          const bVal = b[value] != null && b[value] !== '' ? +b[value] : Infinity
          return aVal - bVal
        }
        if (value === 'cleandex') {
          const aVal = a.cleandex != null ? a.cleandex : -Infinity
          const bVal = b.cleandex != null ? b.cleandex : -Infinity
          return aVal - bVal
        }
        if (value === 'temperature' || value === 'humidity') {
          const aVal = a[value] != null && a[value] !== '' ? Number(a[value]) : Infinity
          const bVal = b[value] != null && b[value] !== '' ? Number(b[value]) : Infinity
          return aVal - bVal
        }
        if (value === 'visits_since_cleaned') return (b.pastVisits ?? 0) - (a.pastVisits ?? 0)
        if (typeof a[value] === 'string' && typeof b[value] === 'string') {
          if (a[value] === '') return 1
          if (b[value] === '') return -1
          if (a[value] < b[value]) return -1
          if (a[value] > b[value]) return 1
          return 0
        }
        if (typeof a[value] === 'object' && !!a[value])
          return !b[value] || !a[value] ? -1 : new Date(b[value].time) - new Date(a[value].time)
        if (value === 'time_start' || value === 'duration') return new Date(b.openTime) - new Date(a.openTime) // Sorting date columns
        if (value === 'date') return new Date(b.date) - new Date(a.date)
        if (value === 'call') return Object.keys(b.complaints).length - Object.keys(a.complaints).length // Sort by object length
      })
    },
    isTablet() {
      if (screen.width === 1280) {
        return true
      } else {
        return false
      }
    },
    openComplaintsForDialog() {
      if (!this.selectedRoom || !this.selectedRoom.complaints) return []
      const list = Array.isArray(this.selectedRoom.complaints) ? this.selectedRoom.complaints : []
      return [...list].sort((a, b) => new Date(a.time) - new Date(b.time))
    },
    closedComplaintsForDialog() {
      return [...this.closedComplaints].sort((a, b) => new Date(a.time) - new Date(b.time))
    },
    /** All events (open + closed) in time order for one-row timeline story */
    roomHistoryTimeline() {
      const open = this.openComplaintsForDialog.map((c, i) => ({ ...c, isOpen: true, key: `open-${i}` }))
      const closed = this.closedComplaintsForDialog.map((c, i) => ({ ...c, isOpen: false, key: `closed-${i}` }))
      return [...open, ...closed].sort((a, b) => new Date(a.time) - new Date(b.time))
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.real-time-table-wrapper {
  width: 100%;
  position: relative;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &--mobile {
    overflow-x: visible;
  }
}

.real-time-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 2px 0 8px;
}

.room-detail-inline--mobile {
  margin: -4px 0 6px;
  border-radius: 0 0 10px 10px;
  border: 1px solid #e2e8f0;
  border-top: none;
}

.real-time-table {
  width: 100%;
  min-width: 0;
  overflow: visible;
  border: 1px solid $grey-border-color;
  border-radius: 8px;
  background: $white;

  .table-row {
    display: grid;
    transition: background-color 0.2s;
    cursor: pointer;
    align-items: center;
    column-gap: 4px;
    min-height: 48px;
    border-bottom: 1px solid $grey-border-color;
    font-size: $font-size-medium;
    @media (width: 1280px) {
      column-gap: 6px;
      font-size: 1.7rem;
      min-height: 80px;
    }

    &:last-child {
      border-bottom: none;
    }

    &.table-head {
      font-weight: $font-weight-bold;
      border-bottom: 2px solid $grey-border-color;
      font-size: $font-size-smaller;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: #6b7280;
      background-color: #f8fafc;
      min-height: 56px;
      height: auto;
      align-items: stretch;
      @media (width: 1280px) {
        font-size: $font-size-large;
        min-height: 72px;
      }

      .table-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 0;
        overflow: hidden;
        box-sizing: border-box;
      }

      .table-head-label {
        display: block;
        width: 100%;
        min-width: 0;
        white-space: normal;
        line-height: 1.25;
        text-align: center;
      }

      .table-cell--name .table-head-label,
      .table-cell--building .table-head-label,
      .table-cell--floor .table-head-label,
      .table-cell--title .table-head-label,
      .table-cell--assignee .table-head-label {
        text-align: left;
      }

      .table-cell--name,
      .table-cell--building,
      .table-cell--floor,
      .table-cell--title,
      .table-cell--assignee {
        justify-content: flex-start;
      }

      .table-cell--cleandex {
        overflow: visible;

        ::v-deep .tooltip .top {
          left: auto;
          right: -10px;
          transform: translateY(-100%);
          @include rtl {
            right: auto;
            left: -10px;
          }
        }

        ::v-deep .tooltip--bottom .top {
          left: auto;
          right: -10px;
          transform: none;
          @include rtl {
            right: auto;
            left: -10px;
          }
        }

        ::v-deep .tooltip .top i {
          left: auto;
          right: 10px;
          margin-left: 0;
          @include rtl {
            right: auto;
            left: 10px;
          }
        }
      }

      .table-cell--cleandex .table-head-label {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        gap: 2px;
        justify-content: center;
        white-space: nowrap;
      }

      .table-head-label .cleandex-header-info {
        display: inline-flex;
        align-items: center;
        margin-inline-start: 2px;
        vertical-align: middle;
        cursor: help;
      }

      .cleandex-info-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        font-size: 12px;
        line-height: 1;
        color: #64748b;
        cursor: help;
        border-radius: 50%;
        flex-shrink: 0;
        pointer-events: auto;
        &:hover {
          color: #475569;
        }
      }
    }

    .table-cell {
      padding: 0 6px;
      min-width: 0; /* allow content to shrink inside grid */
    }

    .table-cell--name,
    .table-cell--building,
    .table-cell--floor,
    .table-cell--title,
    .table-cell--assignee {
      text-align: left;
    }

    .table-cell--assignee {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .table-cell--temperature,
    .table-cell--humidity {
      text-align: center;
      justify-self: center;
      padding: 0 2px;
      font-size: 0.9em;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;

      ::v-deep .real-time-table-cell {
        font-size: inherit;
      }
    }

    .table-cell--temperature .table-head-label,
    .table-cell--humidity .table-head-label {
      white-space: nowrap;
      letter-spacing: 0;
    }

    .table-cell--gender,
    .table-cell--arrival,
    .table-cell--arrival_badges,
    .table-cell--visits_since_cleaned,
    .table-cell--time_start,
    .table-cell--sla_status,
    .table-cell--duration,
    .table-cell--call,
    .table-cell--date,
    .table-cell--last_arrival,
    .table-cell--cleandex {
      text-align: center;
      justify-self: center;
    }

    .table-cell--call {
      min-width: 0;
      overflow: visible;
      justify-self: start;
      padding-inline-start: 0;
      margin-inline-start: -8px;
    }

    .table-cell--cleandex {
      min-width: 3rem;
      overflow: visible;

      ::v-deep .tooltip .top {
        left: auto;
        right: -10px;
        transform: translateY(-100%);
        @include rtl {
          right: auto;
          left: -10px;
        }
      }

      ::v-deep .tooltip .top i {
        left: auto;
        right: 10px;
        margin-left: 0;
        @include rtl {
          right: auto;
          left: 10px;
        }
      }
    }

    .table-cell--visits {
      min-width: 0;
    }

    .buttons-container {
      display: flex;
      align-items: center;
      justify-content: space-around;

      .close-button,
      .start-button,
      .delete-button {
        max-width: 20px;
        transition: opacity 0.2s;
        opacity: 0;
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
      }
      .delete-button img {
        width: 18px;
        height: 18px;
      }
    }

    &.open-comlaint-row:hover {
      background-color: #f5fafe;

      .close-button,
      .start-button,
      .delete-button {
        opacity: 1;
      }
    }
  }

}

.table-cell {
  cursor: pointer;
}

.sorted {
  font-weight: bolder;
}

.sticky-table-head {
  position: sticky;
  top: 0;
  z-index: 99;
  background-color: #f8fafc;
  box-shadow: 0 1px 0 0 $grey-border-color;
}

/* Expanded row: spans all columns, sits in table flow — no overlay, no scroll */
.room-detail-inline {
  grid-column: 1 / -1;
  background: #f8fafc;
  border-bottom: 1px solid $grey-border-color;
  padding: 0;
}

.table-row--expanded {
  border-bottom-color: transparent;
}

.room-detail-inline-inner {
  padding: 16px 20px;
  max-width: 100%;
}

.room-detail-inline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.room-detail-inline-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.room-detail-inline-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  padding: 0 4px;
  &:hover {
    color: #1e293b;
  }
}

.room-detail-inline-meta {
  margin: 0 0 14px;
  font-size: 0.8125rem;
  color: #64748b;
}

.room-detail-inline-timeline {
  margin-top: 4px;
}

.room-detail-inline-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 8px;
}

.room-detail-inline-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  min-height: 32px;
}

.room-detail-inline-arrow {
  margin: 0 4px;
  color: #94a3b8;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.room-detail-inline-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #334155;
  flex-shrink: 0;
}

.room-detail-inline-chip--open {
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.4);
}

.room-detail-inline-chip-tooltip {
  display: inline-flex !important;
  align-items: center;
  min-width: 100%;
}

.room-detail-inline-chip-content {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.room-detail-inline-chip-open-badge {
  margin-inline-start: 2px;
  font-size: 0.625rem;
  opacity: 0.95;
}

.room-detail-inline-chip-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.room-detail-inline-loading,
.room-detail-inline-empty {
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}
</style>
